import { selectTextOptions } from "@/store/canvas/canvasSelectors";
import { setTextOptions } from "@/store/canvas/canvasSlice";
import { type RefObject, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BaseCanvasRef } from "../components/BaseCanvas";
import { DEFAULT_TEXT_OPTIONS } from "../constants/canvasConstants";
import {
  createAddTextObjectAction,
  createModifyTextObjectAction,
  TextUpdateHandler,
} from "../extensions/textExtension";
import { useSubscribeToAddObjectEvent } from "./event/useAddObjectEvent";
import { useSubscribeToChangeTextOptionsEvent } from "./event/useTextChangeOptionsEvent";
import { TextOptions } from "../types/textTypes";

export function useTextAddingAndModification(baseCanvasRef: RefObject<BaseCanvasRef>) {
  const dispatch = useDispatch();
  const textOptionsRef = useRef<TextOptions | null>(null);
  const textOptions = useSelector(selectTextOptions);

  useMemo(() => {
    textOptionsRef.current = textOptions;
  }, [textOptions]);

  useSubscribeToAddObjectEvent(({ type }) => {
    if (type !== "text") return;

    dispatch(setTextOptions(DEFAULT_TEXT_OPTIONS));
    baseCanvasRef.current?.addObject(createAddTextObjectAction(DEFAULT_TEXT_OPTIONS));
  });

  useSubscribeToChangeTextOptionsEvent((textOptions) => {
    if (!textOptions) return;

    baseCanvasRef.current?.modifyObject(createModifyTextObjectAction(textOptions));
  });

  const handleTextOptionsUpdate: TextUpdateHandler = (params) => {
    if (!textOptionsRef.current) return;

    textOptionsRef.current = {
      ...textOptionsRef.current,
      ...params,
    };

    dispatch(setTextOptions(textOptionsRef.current));
  };

  return {
    handleTextOptionsUpdate,
  };
}
